/**
 * Adapter: backend `GET /scenarios/:id` payload → frontend `ScenarioStep[]` shape
 * compatible with the existing in-memory `practiceScenarioRegistry.ts`.
 *
 * Rationale (EXK-235 Step 1b): the backend canonical shape (Prisma Scenario +
 * ScenarioStep + DecisionOption + scenario_steps.ui_metadata JSONB) carries
 * the same authoring data the frontend used in `src/data/scenarios/*.ts`.
 * This adapter materializes the legacy-typed view so consumers (SurgeryCockpit,
 * etc.) do not need any prop/type change to start consuming real backend data.
 *
 * Pure functions only — no React, no fetch, no logging. Easy to unit-test.
 *
 * Forward-compat: as new backend fields land (e.g. additional uiMetadata keys),
 * extend `BackendStep` + the mapping below in a single place.
 */

import type { ScenarioStep, OptionData, Citation } from '../lib/scoring/types';

// ── Backend response shapes (subset we consume) ─────────────────────────────

export interface BackendOption {
  id: number;
  label: string;
  feedback: string | null;
  pointsAwarded: number;
  isCriticalError: boolean;
  orderIndex: number;
}

/**
 * Mirror of `scenario_steps.ui_metadata` JSONB persisted at seed time
 * (EXK-235 Step 1a backend mapper). All keys are optional — legacy rows
 * pre-1a have null ui_metadata, and only seeded scenarios populate it.
 */
export interface BackendStepUiMetadata {
  phase?: string;
  question?: string;
  vitalChanges?: Record<string, number | string | undefined>;
  statusChange?: 'stable' | 'warning' | 'critical' | null;
  triggerInterruption?: {
    severity: 'INFO' | 'WARNING' | 'CRITICAL';
    source: string;
    message: string;
    actions: string[];
  };
  complicationPrevented?: string;
  complicationIfWrong?: {
    name: string;
    probability: string;
    consequence: string;
    prognosisDrop: number;
  };
  optionsMeta?: Array<{
    id: string;
    correct: boolean;
    critical?: boolean;
    reference?: string;
    /**
     * Structured citations from the backend mapper (PR #75 API-2 enrichment —
     * `dominoris-api` v3.39.4+). Preferred over the free-form `reference`
     * string; coexist for back-compat with pre-enrichment snapshots.
     */
    citations?: Citation[];
  }>;
  /** Original frontend stepType when aliased on the backend (e.g. 'EMERGENCY'→'DECISION'). */
  frontendStepType?: string;
}

export interface BackendStep {
  id: number;
  orderIndex: number;
  title: string;
  clinicalContext: string | null;
  stepType: string;
  competencyDimension: string;
  maxPoints: number;
  weight: number;
  timeLimitSeconds: number | null;
  isCritical: boolean;
  options: BackendOption[];
  /** Present from EXK-235 Step 1a onward; null for legacy seeds. */
  uiMetadata?: BackendStepUiMetadata | null;
}

export interface BackendScenarioDetail {
  id: number;
  title: string;
  description: string | null;
  specialty: string;
  difficulty: string;
  targetLevel: string | null;
  compressedHours: number;
  estimatedMinutes: number | null;
  tags: string[];
  version: number;
  isActive: boolean;
  steps: BackendStep[];
}

// ── Adapter contract error (EXK-235 F2.2 typed exception) ───────────────────

export type AdapterContractErrorCode = 'MISSING_OPTIONS_META' | 'PARTIAL_OPTIONS_META';

/**
 * Thrown by `backendToScenarioStep` when the backend payload violates the
 * `uiMetadata.optionsMeta` contract (EXK-235 APP-3 E5). Consumers narrow via
 * `instanceof AdapterContractError` instead of message-string regex —
 * resilient to copy changes and friendly to TypeScript narrowing.
 *
 * Still extends `Error`, so ErrorBoundary / existing catch sites that only
 * check `instanceof Error` keep working.
 */
export class AdapterContractError extends Error {
  readonly code: AdapterContractErrorCode;
  readonly stepId: number;
  readonly optionLetter: string | null;

  constructor(args: {
    code: AdapterContractErrorCode;
    stepId: number;
    optionLetter?: string | null;
    message: string;
  }) {
    super(args.message);
    this.name = 'AdapterContractError';
    this.code = args.code;
    this.stepId = args.stepId;
    this.optionLetter = args.optionLetter ?? null;
  }
}

// ── Adapter (pure) ───────────────────────────────────────────────────────────

/**
 * Map one backend step into the legacy `ScenarioStep` UI shape.
 *
 * Field resolution order (favoring authoring fidelity when uiMetadata exists):
 *   - `question`  ← uiMetadata.question (full original) || backend `title` (first sentence)
 *   - `context`   ← backend `clinicalContext` || ''
 *   - `phase`     ← uiMetadata.phase || backend `competencyDimension`
 *   - `stepType`  ← uiMetadata.frontendStepType || backend `stepType`
 *   - `vitalChanges`, `statusChange`, `triggerInterruption`,
 *     `complicationPrevented`, `complicationIfWrong` ← uiMetadata (best-effort)
 *
 * Option-level `correct`/`reference`/`critical` are sourced from
 * `uiMetadata.optionsMeta` keyed by 1-based letter id ('a','b','c','d')
 * matching the backend `orderIndex` (0,1,2,3 → 'a','b','c','d').
 * When `optionsMeta` is absent, we fall back to deriving `correct` from
 * `!isCriticalError` (best-effort for legacy seeds).
 */
export function backendToScenarioStep(
  backendStep: BackendStep,
  index: number,
): ScenarioStep {
  const ui = backendStep.uiMetadata ?? null;
  // EXK-235 APP-3 E5 — backend scenarios MUST carry `uiMetadata.optionsMeta`.
  // The API mapper enrichment (PR #75 API-2, dominoris-api v3.39.4) writes
  // the author markers (`correct` / `critical` / `reference` / `citations`)
  // for every seeded option. Without optionsMeta the adapter would have to
  // derive `correct` / `critical` from the backend's `isCriticalError`
  // heuristic — exactly the silent-wrong-scenario class APP-3 closes. Hard-
  // fail pushes the contract violation into ops (re-seed via the
  // `REPLACE=1` CLI from PR #75) instead of letting a misseeded row render
  // with invented option semantics.
  if (!ui?.optionsMeta) {
    throw new AdapterContractError({
      code: 'MISSING_OPTIONS_META',
      stepId: backendStep.id,
      message: `Backend scenario step ${backendStep.id} ("${backendStep.title}") missing uiMetadata.optionsMeta — refusing to derive option semantics heuristically (EXK-235 APP-3 E5). Re-seed via dominoris-api scripts/seed-estudio-scenarios.ts with REPLACE=1.`,
    });
  }
  const optionsMetaById = new Map<string, NonNullable<BackendStepUiMetadata['optionsMeta']>[number]>();
  for (const m of ui.optionsMeta) optionsMetaById.set(m.id, m);

  const stepType = ui?.frontendStepType ?? backendStep.stepType;

  // Mapping legacy letter id ('a'/'b'/...) → backend numeric option id, used by
  // `useSessionLifecycle.submitDecision` to identify which option the user
  // selected against the backend's option ids. Built in lock-step with the
  // `options` array below.
  const backendOptionIds: Record<string, number> = {};

  // EXK-235 APP-2 — sort by authoring `orderIndex` before mapping, so letter
  // ids ('a'/'b'/'c'/'d') and the `backendOptionIds` lookup follow the
  // authoring sequence regardless of the API return order. Without this, an
  // API that emits options ordered by id (or any other order) would silently
  // shuffle the displayed sequence and the option-id mapping.
  const sortedOptions = [...backendStep.options].sort(
    (a, b) => a.orderIndex - b.orderIndex,
  );
  const options: OptionData[] = sortedOptions.map((opt, optIdx) => {
    const letterId = String.fromCharCode('a'.charCodeAt(0) + optIdx);
    const meta = optionsMetaById.get(letterId);
    // EXK-235 APP-3 E5 — per-letter hard-fail. `optionsMeta` may exist but
    // miss the entry for a given letter (partial author meta — incomplete
    // enrichment for a specific option). Treating that as `!isCriticalError`
    // would still invent semantics for the missing option.
    if (!meta) {
      throw new AdapterContractError({
        code: 'PARTIAL_OPTIONS_META',
        stepId: backendStep.id,
        optionLetter: letterId,
        message: `Backend scenario step ${backendStep.id} ("${backendStep.title}") option "${letterId}" missing optionsMeta entry — author marker required (EXK-235 APP-3 E5).`,
      });
    }
    const correct = meta.correct;
    // Codex P0: SurgeryCockpit reads `!!option.critical` to register critical-
    // error penalty + prognosis drop. Authoring marker `meta.critical` wins;
    // backend's `isCriticalError` is the secondary signal when meta omits the
    // boolean (legacy enrichment).
    const critical = meta.critical ?? opt.isCriticalError;
    const explanation = opt.feedback ?? '';
    backendOptionIds[letterId] = opt.id;
    return {
      id: letterId,
      text: opt.label,
      points: opt.pointsAwarded,
      correct,
      ...(critical ? { critical: true } : {}),
      ...(meta.reference !== undefined ? { reference: meta.reference } : {}),
      // EXK-235 APP-3 — propagate structured citations from the backend
      // mapper (PR #75 API-2). `reference` and `citations` coexist for
      // back-compat with pre-enrichment snapshots.
      ...(meta.citations !== undefined ? { citations: meta.citations } : {}),
      explanation,
    };
  });

  return {
    // EXK-235 APP-3 E1 discriminator — `source: 'backend'` narrows downstream
    // type-checks to `BackendScenarioStep`, guaranteeing the required backend
    // ids are present without per-consumer `undefined` checks.
    source: 'backend' as const,
    // 1-based id stays consistent with the legacy authoring convention.
    id: index + 1,
    phase: ui?.phase ?? backendStep.competencyDimension,
    question: ui?.question ?? backendStep.title,
    context: backendStep.clinicalContext ?? '',
    options,
    step: {
      maxPoints: backendStep.maxPoints,
      weight: backendStep.weight,
      // EXK-235 APP-2 — preserve `null` so SurgeryCockpit's
      // `currentStepData.step.timeLimitSeconds ?? 120` default fires. Coercing
      // to 0 here would skip the nullish coalesce and expire the timer
      // immediately on every "no time limit" step.
      timeLimitSeconds: backendStep.timeLimitSeconds,
      stepType,
      competencyDimension: backendStep.competencyDimension,
    },
    vitalChanges: ui?.vitalChanges ?? {},
    statusChange: ui?.statusChange ?? null,
    ...(ui?.triggerInterruption ? { triggerInterruption: ui.triggerInterruption } : {}),
    ...(ui?.complicationPrevented ? { complicationPrevented: ui.complicationPrevented } : {}),
    ...(ui?.complicationIfWrong ? { complicationIfWrong: ui.complicationIfWrong } : {}),
    // Backend ids — required on the backend variant of the discriminated union.
    backendStepId: backendStep.id,
    backendOptionIds,
  };
}

/**
 * Map a full backend scenario detail to the legacy `ScenarioStep[]`.
 *
 * Steps are renumbered by array position (1-based) regardless of backend `id`,
 * matching the legacy authoring convention where positional id is part of the
 * UX flow.
 */
export function backendToScenarioSteps(
  backendScenario: BackendScenarioDetail,
): ScenarioStep[] {
  return backendScenario.steps.map((s, idx) => backendToScenarioStep(s, idx));
}

// ── Summary list shape (GET /scenarios list response) ───────────────────────

export interface BackendScenarioSummary {
  id: number;
  title: string;
  description: string | null;
  specialty: string;
  difficulty: string;
  targetLevel: string | null;
  compressedHours: number;
  estimatedMinutes: number | null;
  tags: string[];
  version: number;
  isActive: boolean;
  stepsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface BackendPaginatedResponse<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}
